// ── Trail network types and constants for the 25x overworld ──
//
// Trail tiles render OVER the base tilemap as a dedicated layer. Each tier
// has a distinct visual language per realm (see wayfinding-navigation.md §1).
// The topology data drives both server-side tile placement and client-side
// color rendering.
import { RealmId } from './realms.js';
// ── Trail tier enum ──
/** Trail tiers from most prominent to least visible. */
export var TrailTier;
(function (TrailTier) {
    /** 3-4 tile width, worn cobblestone, lanterns every ~60 tiles. */
    TrailTier["PilgrimRoad"] = "pilgrim_road";
    /** 2-3 tile width, packed earth biome-tinted, wooden markers. */
    TrailTier["RealmPath"] = "realm_path";
    /** 1-2 tile width, faint grass-worn line, scattered stones. */
    TrailTier["WanderTrail"] = "wander_trail";
    /** 1 tile width, 5% lighter tint, no markers. */
    TrailTier["GhostPath"] = "ghost_path";
})(TrailTier || (TrailTier = {}));
/** Trail width in tiles per tier. */
export const TRAIL_WIDTHS = {
    [TrailTier.PilgrimRoad]: { min: 3, max: 4 },
    [TrailTier.RealmPath]: { min: 2, max: 3 },
    [TrailTier.WanderTrail]: { min: 1, max: 2 },
    [TrailTier.GhostPath]: { min: 1, max: 1 },
};
/** Interval (in tiles) between stone lanterns on Pilgrim Roads. */
export const PILGRIM_ROAD_LANTERN_INTERVAL = 60;
// ── Trail tile GID space ──
//
// Trail tiles occupy GIDs 100-199 to avoid colliding with the base tilemap
// GID space (1-99). Each tier × realm combination gets a block of 4 GIDs
// (center, edge-left, edge-right, marker).
/** Base GID for trail tiles. */
export const TRAIL_GID_BASE = 100;
/** Number of GIDs allocated per tier-realm combination. */
export const TRAIL_GIDS_PER_VARIANT = 4;
/** GID slot offsets within a tier-realm block. */
export var TrailGidSlot;
(function (TrailGidSlot) {
    /** Center tile — the main trail surface. */
    TrailGidSlot[TrailGidSlot["Center"] = 0] = "Center";
    /** Left edge tile — transition to base terrain. */
    TrailGidSlot[TrailGidSlot["EdgeLeft"] = 1] = "EdgeLeft";
    /** Right edge tile — transition to base terrain. */
    TrailGidSlot[TrailGidSlot["EdgeRight"] = 2] = "EdgeRight";
    /** Marker tile — lantern (Pilgrim), wooden post (Realm), stone (Wander). */
    TrailGidSlot[TrailGidSlot["Marker"] = 3] = "Marker";
})(TrailGidSlot || (TrailGidSlot = {}));
/** Ordered tier list for GID block assignment. */
const TIER_ORDER = [
    TrailTier.PilgrimRoad,
    TrailTier.RealmPath,
    TrailTier.WanderTrail,
    TrailTier.GhostPath,
];
/** Ordered realm list for GID block assignment. */
const REALM_ORDER = [
    RealmId.VerdanaFields,
    RealmId.SablewindReaches,
    RealmId.FrostmereExpanse,
    RealmId.EmberMire,
];
/**
 * Returns the base GID for a given tier × realm combination.
 * Layout: TRAIL_GID_BASE + (tierIndex * 4 realms + realmIndex) * TRAIL_GIDS_PER_VARIANT
 */
export function trailGidBase(tier, realm) {
    const tierIdx = TIER_ORDER.indexOf(tier);
    const realmIdx = REALM_ORDER.indexOf(realm);
    return TRAIL_GID_BASE + (tierIdx * REALM_ORDER.length + realmIdx) * TRAIL_GIDS_PER_VARIANT;
}
/**
 * Returns the specific GID for a trail tile variant.
 */
export function trailGid(tier, realm, slot) {
    return trailGidBase(tier, realm) + slot;
}
/**
 * Checks if a GID is a trail tile.
 */
export function isTrailGid(gid) {
    return gid >= TRAIL_GID_BASE && gid < TRAIL_GID_BASE + TIER_ORDER.length * REALM_ORDER.length * TRAIL_GIDS_PER_VARIANT;
}
/**
 * Decodes a trail GID back into its tier, realm, and slot.
 * Returns null if the GID is not a trail tile.
 */
export function decodeTrailGid(gid) {
    if (!isTrailGid(gid))
        return null;
    const offset = gid - TRAIL_GID_BASE;
    const blockIndex = Math.floor(offset / TRAIL_GIDS_PER_VARIANT);
    const slot = (offset % TRAIL_GIDS_PER_VARIANT);
    const tierIdx = Math.floor(blockIndex / REALM_ORDER.length);
    const realmIdx = blockIndex % REALM_ORDER.length;
    return { tier: TIER_ORDER[tierIdx], realm: REALM_ORDER[realmIdx], slot };
}
/** Trail tile colors: tier × realm → color set (hex). */
export const TRAIL_TILE_COLORS = {
    [TrailTier.PilgrimRoad]: {
        [RealmId.VerdanaFields]: { center: 0x8a7a6a, edgeLeft: 0x9a8a7a, edgeRight: 0x9a8a7a, marker: 0xc8b090 },
        [RealmId.SablewindReaches]: { center: 0x9a8a6a, edgeLeft: 0xaa9a7a, edgeRight: 0xaa9a7a, marker: 0xd0b878 },
        [RealmId.FrostmereExpanse]: { center: 0x7a8a9a, edgeLeft: 0x8a9aaa, edgeRight: 0x8a9aaa, marker: 0xa0b8d0 },
        [RealmId.EmberMire]: { center: 0x6a5a4a, edgeLeft: 0x7a6a5a, edgeRight: 0x7a6a5a, marker: 0x907860 },
    },
    [TrailTier.RealmPath]: {
        [RealmId.VerdanaFields]: { center: 0x6b7a3a, edgeLeft: 0x7b8a4a, edgeRight: 0x7b8a4a, marker: 0x8a6a3a },
        [RealmId.SablewindReaches]: { center: 0x8a7a4a, edgeLeft: 0x9a8a5a, edgeRight: 0x9a8a5a, marker: 0x7a6a3a },
        [RealmId.FrostmereExpanse]: { center: 0x6a7a8a, edgeLeft: 0x7a8a9a, edgeRight: 0x7a8a9a, marker: 0x5a6a7a },
        [RealmId.EmberMire]: { center: 0x5a4a3a, edgeLeft: 0x6a5a4a, edgeRight: 0x6a5a4a, marker: 0x7a4a2a },
    },
    [TrailTier.WanderTrail]: {
        [RealmId.VerdanaFields]: { center: 0x5a9c4f, edgeLeft: 0x6aac5f, edgeRight: 0x6aac5f, marker: 0x7a8a6a },
        [RealmId.SablewindReaches]: { center: 0x9a8a5a, edgeLeft: 0xaa9a6a, edgeRight: 0xaa9a6a, marker: 0x8a7a5a },
        [RealmId.FrostmereExpanse]: { center: 0x8a9aaa, edgeLeft: 0x9aaaba, edgeRight: 0x9aaaba, marker: 0x7a8a9a },
        [RealmId.EmberMire]: { center: 0x4a3a2a, edgeLeft: 0x5a4a3a, edgeRight: 0x5a4a3a, marker: 0x6a4a3a },
    },
    [TrailTier.GhostPath]: {
        [RealmId.VerdanaFields]: { center: 0x5aac5a, edgeLeft: 0x5aac5a, edgeRight: 0x5aac5a, marker: 0x5aac5a },
        [RealmId.SablewindReaches]: { center: 0xa89a6a, edgeLeft: 0xa89a6a, edgeRight: 0xa89a6a, marker: 0xa89a6a },
        [RealmId.FrostmereExpanse]: { center: 0x9aaab8, edgeLeft: 0x9aaab8, edgeRight: 0x9aaab8, marker: 0x9aaab8 },
        [RealmId.EmberMire]: { center: 0x5a4a3e, edgeLeft: 0x5a4a3e, edgeRight: 0x5a4a3e, marker: 0x5a4a3e },
    },
};
/** Name of the dedicated trail tile layer in the tilemap. */
export const TRAIL_LAYER_NAME = 'trails';
/** Speed multipliers per trail tier (1.0 = normal walking speed). */
export const TRAIL_SPEED_MULTIPLIER = {
    [TrailTier.PilgrimRoad]: 1.15,
    [TrailTier.RealmPath]: 1.08,
    [TrailTier.WanderTrail]: 1.0,
    [TrailTier.GhostPath]: 1.0,
};
/** Speed multiplier when off-trail (slightly slower — brush/undergrowth). */
export const OFF_TRAIL_SPEED_MULTIPLIER = 0.92;
//# sourceMappingURL=trails.js.map